import { ReactElement, useCallback, useEffect, useState } from 'react'
import { DetailsTemplate } from '.'
import { useBFSM } from '../../contexts/bfsmContext'
import { useBFSMValue } from '../../services/hooks/useBFSMValues'
import {
    BasisActions,
    BasisState,
    BookingReason,
    BookingReasons,
    CalendarActions,
    CalendarState,
    Company,
    CompanyActions,
    CompanyLead,
    CompanyLeadState,
    DetailActions,
    DetailsState,
    GeneralActions,
    GiftCardProviders,
    GoDreamGiftCard,
    GiftCard,
    ThirdPartyGiftCard,
    ICreateCompanyLeadDTO,
    OnDemand,
    Package,
    PackageActions,
    PackageState,
    PackageTemplate,
    IAppliedDiscount,
} from '@raskrask-dk/bookingflow-state-machine'
import { Validators } from '@raskrask-dk/raskrask-utils'
import { useAppDispatch, useAppSelector } from '../../redux/hooks'
import {
    getLandCodes,
    getShowHelp,
    setShowHelp,
} from '../../redux/reducers/detailsSettings'
import { adaptorToGetPaymentInfoOnDetailsState } from './adaptors'
import { ProviderOption, useDetails } from './hooks'
import { DetailsFields } from './props'
import { getLandCode, setLandCode } from '../../redux/reducers/customerDetails'
import { useServiceCombinations } from '../../contexts/serviceCombinationsContext'
import { arrayToggleBookingReasonsItem } from '../../services/array'
import { setErrorcase } from '../../redux/reducers/appSettings'
import { RedirectionToConfirmation } from '../../services/confirmationredirect'
import { PartnerBF } from '../../models'
import {
    CodeType,
    CodeTypeOptions,
    RR3Providers,
} from '../chemistry/detailsComponents/Codes/CodeValidator/props'
import { useTranslation } from 'react-i18next'

const DetailsPage = (): ReactElement => {
    const [BFSM] = useBFSM()
    const dispatch = useAppDispatch()
    const { t } = useTranslation()

    const [antiSpam, setAntiSpam] = useState(false)

    const serviceCombinations = useServiceCombinations()
    //useBFSMValue
    const [serviceType] = useBFSMValue<
        CalendarState,
        CalendarActions.SetServiceType,
        'serviceType'
    >('serviceType')
    const [calendarPartner] = useBFSMValue<
        CalendarState,
        CalendarActions.SetPartner,
        'partner'
    >('partner')
    const [goal] = useBFSMValue<CalendarState, CalendarActions.SetGoal, 'goal'>(
        'goal'
    )
    const [packagePartner] = useBFSMValue<
        PackageState,
        PackageActions.SetPartner,
        'partner'
    >('partner')
    const partner = packagePartner
        ? packagePartner
        : (calendarPartner as PartnerBF)

    const [isAccepted, setIsAccepted] = useBFSMValue<
        DetailsState,
        DetailActions.AcceptConditions,
        'acceptConditions'
    >('acceptConditions', DetailActions.AcceptConditions)
    const [basisType] = useBFSMValue<
        BasisState,
        BasisActions.SetBasisType,
        'basisType'
    >('basisType')
    const [customerEmail, setCustomerEmail] = useBFSMValue<
        DetailsState,
        DetailActions.SetCustomerEmail,
        'customerEmail'
    >('customerEmail', DetailActions.SetCustomerEmail)
    const [customerPhone, setCustomerPhone] = useBFSMValue<
        DetailsState,
        DetailActions.SetCustomerPhone,
        'customerPhone'
    >('customerPhone', DetailActions.SetCustomerPhone)
    const [customerName, setCustomerName] = useBFSMValue<
        DetailsState,
        DetailActions.SetCustomerName,
        'customerName'
    >('customerName', DetailActions.SetCustomerName)
    const [companyAdminName, setCompanyAdminName] = useBFSMValue<
        DetailsState,
        DetailActions.SetCompanyAdminName,
        'companyAdminName'
    >('companyAdminName', DetailActions.SetCompanyAdminName)
    const [companyName, setCompanyName] = useBFSMValue<
        DetailsState,
        DetailActions.SetCompanyName,
        'companyName'
    >('companyName', DetailActions.SetCompanyName)
    const [companySettings] = useBFSMValue<
        CompanyLeadState,
        CompanyActions.SetCompanySettings,
        'companySettings'
    >('companySettings', CompanyActions.SetCompanySettings)
    const [selectedBookingReasons = [], setSelectedBookingReasons] =
        useBFSMValue<
            DetailsState,
            DetailActions.SetBookingReasons,
            'bookingReasons'
        >('bookingReasons', DetailActions.SetBookingReasons)
    const [problemDescription, setProblemDescription] = useBFSMValue<
        DetailsState,
        DetailActions.SetProblemDescription,
        'problemDescription'
    >('problemDescription', DetailActions.SetProblemDescription)
    const [parkingText, setParkingText] = useBFSMValue<
        DetailsState,
        DetailActions.SetParkingText,
        'parkingText'
    >('parkingText', DetailActions.SetParkingText)
    const [specialCare, setSpecialCare] = useBFSMValue<
        DetailsState,
        DetailActions.SetSpecialNeeds,
        'specialNeeds'
    >('specialNeeds', DetailActions.SetSpecialNeeds)

    //BFSM.getData
    const { product } = BFSM.getData<DetailsState>()
    const { customerType } = BFSM.getData<BasisState>()
    const isCompanyBooking = customerType === 'Company'

    //useAppSelector
    const landCode = useAppSelector(getLandCode)
    const landCodes = useAppSelector(getLandCodes)
    const showHelp = useAppSelector(getShowHelp)

    //useState
    const [
        submitClickedWithMissingDetails,
        setSubmitClickedWithMissingDetails,
    ] = useState(false)
    const [showError, setShowError] = useState(false)
    const [isDiscountApplicable, setDiscountNotApplicable] = useState(false)
    const isFreeConsultation =
        serviceType !== undefined ? serviceType.freeConsultation : false
    const isCompanyLead = product instanceof CompanyLead
    const [isDiscountSubmitable, setIsDiscountSubmitable] = useState(false)
    const [bookingReasons, setBookingReasons] = useState<BookingReasons>([])

    //BFSM.canDo
    const canSetDescription = BFSM.canDo(DetailActions.SetProblemDescription)
    const canSetParking =
        BFSM.canDo(DetailActions.SetParkingText) && !isFreeConsultation
    const canSetBookingReasons = BFSM.canDo(DetailActions.SetBookingReasons)
    const canSetSpecialCare = BFSM.canDo(DetailActions.SetSpecialNeeds)
    const canGoToPayment = BFSM.canDo(DetailActions.Next)

    const fieldArray = (): DetailsFields[] => {
        switch (product.constructor) {
            case Package:
                return [
                    'customerName',
                    'customerEmail',
                    'customerPhone',
                    'terms',
                ]
            case PackageTemplate:
                return [
                    'customerName',
                    'customerEmail',
                    'customerPhone',
                    'terms',
                ]
            case CompanyLead:
                return [
                    'companyName',
                    'contactName',
                    'customerEmail',
                    'customerPhone',
                    'companyLeadSummary',
                    'terms',
                ]
            case OnDemand:
                return []
            default:
                if (customerType === 'Company') {
                    // CompanyOneOff
                    return [
                        'codes',
                        'contactName',
                        'customerEmail',
                        'customerPhone',
                        'terms',
                        'additionalFields',
                        'companyName',
                    ]
                } else {
                    // Private Booking
                    return [
                        'customerName',
                        'customerEmail',
                        'customerPhone',
                        'codes',
                        'terms',
                        'additionalFields',
                        'returningCustomerDiscount',
                    ]
                }
        }
    }
    const bR = serviceCombinations.basisTypes[
        `${basisType?.id}_${`InPerson`}_${customerType}`
    ].bookingReasons.map((br) => {
        const { bookingReasonId: id, bookingReasonName: name } =
            serviceCombinations.bookingReasons[br]
        return new BookingReason(id, name)
    })

    const onClickBookingReason = (bookingReason: BookingReason) => {
        // use arrayToggleBookingReasonsItem instead of arrayToggleItem as BookingReason does not have primitive values.
        const newSelectedReasons = arrayToggleBookingReasonsItem(
            selectedBookingReasons ? selectedBookingReasons : [],
            bookingReason
        )
        setSelectedBookingReasons(newSelectedReasons)
        //refresh the booking reasons so the child component rerenders, and the buttons switch styles.
        setBookingReasons(bR)
    }

    const onChangeLandCode = (e: LabelLandCode): void => {
        dispatch(setLandCode(e))
    }

    const finalizeCompanyLead = async (): Promise<void> => {
        try {
            const data = BFSM.getData<DetailsState>()
            const company = new Company(
                data.customerPhone!,
                data.companyAdminName!,
                data.companyName!,
                data.customerEmail!
            )
            const lead = data.product as CompanyLead
            const dto: ICreateCompanyLeadDTO = {
                company: {
                    adminName: company.adminName,
                    companyName: company.companyName,
                    customerType: company.type,
                    email: company.email,
                    phone: company.phone,
                },
                lead: {
                    address: lead.address,
                    settings: lead.settings,
                    type: lead.type,
                },
            }
            const response = await BFSM.repo.createCompanyLead(dto)
            const firstName =
                data.companyAdminName?.split(' ') || data.companyAdminName || '' // this has to split on a WHITESPACE !!!
            if (response.status === 200)
                RedirectionToConfirmation.confirmation_companyLead(firstName[0])
        } catch (err) {
            dispatch(setErrorcase('FinalizeError'))
            BFSM.doAction(GeneralActions.GoToErrorState, undefined)
        }
    }

    const onClickGoToPayment = () => {
        try {
            if (canGoToPayment) {
                if (product.type === 'CompanyLead') {
                    if (antiSpam) return
                    if (!antiSpam) setAntiSpam(true)
                    finalizeCompanyLead()
                    return
                } else {
                    BFSM.doAction(DetailActions.Next, undefined)
                }
                canGoToPayment
            } else {
                setSubmitClickedWithMissingDetails(true)
            }
        } catch (err) {
            // TODO! Unhandled error. What should happen here?
            console.log(err)
        }
    }

    const resetDiscount = () => {
        setGiftCardCode(undefined)
        setPackageCode(undefined)
        setPromoCode(undefined)
        if (discountAmount > 0) {
            updatePrice()
        }
    }

    const updatePrice = async () => {
        if (isCompanyLead) return
        const data = BFSM.getData<DetailsState>()
        const paymentInfo = adaptorToGetPaymentInfoOnDetailsState(data, false)
        const result = await BFSM.repo.getPaymentInfo(paymentInfo)
        const isAllegeableRC =
            result?.discountList?.type === 'ReturningCustomerDiscount' &&
            result?.discountList?.amount !== 0
        const displayedDiscount = discountMapper(result.discountList)
        setIsAllegeable(isAllegeableRC)
        setPrice(result.price)
        setDiscountAmount(displayedDiscount)
        setDiscountNotApplicable(true)
    }

    const discountMapper = (discountList: IAppliedDiscount | undefined) => {
        if (discountList === undefined) return 0
        if (
            giftCardCode !== undefined &&
            discountList.type === 'CardCodeDiscount'
        )
            return discountList.amount
        if (
            packageCode !== undefined &&
            discountList.type === 'CardCodeDiscount'
        )
            return discountList.amount
        if (promoCode !== undefined && discountList.type === 'PromoDiscount')
            return discountList.amount
        if (
            packageCode == undefined &&
            giftCardCode === undefined &&
            promoCode === undefined &&
            discountList.type === 'ReturningCustomerDiscount'
        )
            return discountList.amount
        return 0
    }

    const onClickBack = () => {
        if (showHelp) {
            BFSM.goBack()
        } else {
            dispatch(setShowHelp(true))
        }
    }

    const [isAllegeable, setIsAllegeable] = useState(false)

    const returningCustomerProps = {
        headerText: t('details.form.discount12.headerText'),
        discountReasonText: t('details.form.discount12.discountReasonText'),
        valueText: t('details.form.discount12.valueText'),
        hintText: t('details.form.discount12.hintText'),
        isAllegeable: isAllegeable,
    }

    // ###################### CODE-VALIDATION-MAZE ############################

    // Codes in BFSM. Only set these when a valid code is constructed and redeemed!
    const [packageCode, setPackageCode] = useBFSMValue<
        DetailsState,
        DetailActions.SetKlippeCard,
        'klippeCardCode'
    >('klippeCardCode', DetailActions.SetKlippeCard)
    const [giftCardCode, setGiftCardCode] = useBFSMValue<
        DetailsState,
        DetailActions.SetGiftCard,
        'giftCard'
    >('giftCard', DetailActions.SetGiftCard)
    const [promoCode, setPromoCode] = useBFSMValue<
        DetailsState,
        DetailActions.SetPromoCode,
        'promoCode'
    >('promoCode', DetailActions.SetPromoCode)

    const { thirdPartyGiftCardTypes, providerOptions } = useDetails()

    const emailPhoneValid =
        !!customerPhone &&
        Validators.phoneNumberValidator(customerPhone) &&
        !!customerEmail &&
        Validators.emailValidator(customerEmail)
    const canSetCodeCompany =
        !!companyName &&
        companyName !== '' &&
        !!companyAdminName &&
        companyAdminName !== '' &&
        emailPhoneValid
    const canSetCodeNormal =
        !!customerName && customerName !== '' && emailPhoneValid
    const canUseCodeForm = isCompanyBooking
        ? canSetCodeCompany
        : canSetCodeNormal

    const [price, setPrice] = useState<number | undefined>(undefined)
    const [discountAmount, setDiscountAmount] = useState<number>(0)

    const redeemedCodeTextGiftCard = useCallback(() => {
        return t('details.form.codes.priceforcode', {
            price: discountAmount,
        })
    }, [discountAmount, t])
    const redeemedCodeTextPackage = useCallback(() => {
        return t('details.form.codes.valueforpackage', {
            value: discountAmount,
        })
    }, [discountAmount, t])
    const redeemedCodeTextPromo = useCallback(() => {
        return t('details.form.codes.percentageforcode', {
            percentage: (
                (discountAmount / (price! + discountAmount)) *
                100
            ).toFixed(0),
        })
    }, [discountAmount, price, t])

    const codeTypeOptions: CodeTypeOptions = [
        {
            value: 'giftCardCode',
            label: t('details.form.codes.GiftCard'),
            redeemedCodeText: redeemedCodeTextGiftCard,
        },
        {
            value: 'packageCode',
            label: t('details.form.codes.KlippeCard'),
            redeemedCodeText: redeemedCodeTextPackage,
        },
        {
            value: 'promoCode',
            label: t('details.form.codes.PromoCode'),
            redeemedCodeText: redeemedCodeTextPromo,
        },
    ]

    const canUseValidatorText = t('details.form.codes.notfilled')
    const discountErrorMessage = t('details.codes.cannotbeused')
    const formatErrorMessage = t('details.codes.wrongformat')

    const isValid = (value: string, provider: GiftCardProviders) => {
        return Validators.giftCardCodeValidator(value, provider)
    }

    const helpLinkText = (provider: ProviderOption | undefined) => {
        return t('details.form.codes.helplink', {
            provider: provider?.value || '',
        })
    }

    const getGiftCardCodePlaceholder = (
        provider: ProviderOption | undefined
    ): string => {
        const eg = t('common:foranexample')
        const defaultPH = t('details.form.codes.giftcard.placeholder')
        switch (provider?.value) {
            case 'DenBedsteGave':
                return eg + ' bgdk-12345678 | bgdk12345678'
            case 'GoDream':
                return eg + ' 1234567890123'
            case 'GoGift':
                return defaultPH
            case 'MoreBox':
                return defaultPH
            case 'RaskRask':
                return eg + ' RR1234567'
            case 'SendEnTanke':
                return eg + ' abc4efgh8jl'
            case 'SmartBox':
                return eg + ' 123456789'
            default:
                return defaultPH
        }
    }

    const giftCardHandler = {
        labelForProviderSelector: t('details.form.codes.giftcard.choose'),
        placeholderForCode: getGiftCardCodePlaceholder,
        placeholderForActivationCodeInput: t(
            'details.form.codes.giftcard.godream.placeholder'
        ),
        placeholderForCodeValue: t('whichclipcardtype'),
        textForCtaButton: t('details.form.codes.giftcard.submit'),
    }
    const packageHandler = {
        placeholderForCode: `${t(
            'details.form.codes.klippekort.placeholder'
        )}. ${t('common:foranexample')} RR1234567`,
        textForCtaButton: t('details.form.codes.klippekort.submit'),
    }
    const promoHandler = {
        placeholderForCode: t('details.form.codes.promocode.placeholder'),
        textForCtaButton: t('details.form.codes.promocode.submit'),
    }

    const headerText = (codeType: CodeType | undefined) => {
        const key =
            codeType === 'giftCardCode'
                ? 'GiftCard'
                : codeType === 'packageCode'
                ? 'KlippeCard'
                : 'PromoCode'
        return t('details.form.codes.adddedcode', {
            code: t(`details.form.codes.adddedcode.${key}`).toLocaleLowerCase(),
        })
    }

    const redeemedHandler = {
        headerText: headerText,
        hintText: t('details.form.codes.nomultiple'),
    }

    const onCodeRedeem = (
        codeType: CodeType,
        provider: RR3Providers,
        code: string,
        thirdPartyValue: string
    ) => {
        if (codeType === 'giftCardCode') {
            const providerId = thirdPartyValue ? parseInt(thirdPartyValue) : 0
            const ggg =
                provider === 'GoDream'
                    ? new GoDreamGiftCard(
                          code.split(' ')[0],
                          providerId,
                          code.split(' ')[1]
                      )
                    : provider !== 'RaskRask'
                    ? new ThirdPartyGiftCard(code, provider, providerId)
                    : new GiftCard(code, provider)
            setGiftCardCode(ggg)
            return
        } else if (codeType === 'packageCode') {
            setPackageCode(code)
            return
        } else if (codeType === 'promoCode') {
            setPromoCode(code)
        }
    }
    const onRedeemedCodeReset = () => {
        resetDiscount()
    }

    // When we provide a klippekort to the flow it should set the code atuomatically
    const presetCode = packageCode
        ? {
              codeType: 'packageCode' as CodeType,
              provider: 'RaskRask' as GiftCardProviders,
              code: packageCode,
              thirdPartyValue: '',
          }
        : undefined

    const codesProps = {
        canUseCodeForm: canUseCodeForm,
        canUseValidatorText: canUseValidatorText,
        codeTypeOptions: codeTypeOptions,
        discountAmount: discountAmount,
        discountErrorMessage: discountErrorMessage,
        formatErrorMessage: formatErrorMessage,
        giftCardHandler: giftCardHandler,
        helpLinkText: helpLinkText,
        isValid: isValid,
        packageHandler: packageHandler,
        providerOptions: providerOptions,
        promoHandler: promoHandler,
        redeemedHandler: redeemedHandler,
        thirdPartyGiftCardTypes: thirdPartyGiftCardTypes,
        onCodeRedeem: onCodeRedeem,
        onRedeemedCodeReset: onRedeemedCodeReset,
        presetCode: presetCode,
    }

    // ##################################################

    // Bookingreasons selection should be always reseted on reaload
    useEffect(() => {
        resetDiscount()
        setBookingReasons(bR)
    }, [])

    useEffect(() => {
        updatePrice()
    }, [packageCode, giftCardCode, promoCode])

    // Simple Debouncer for updateing discounts when customer types name / email / phone.
    useEffect(() => {
        const getData = setTimeout(() => {
            updatePrice()
        }, 500)
        return () => clearTimeout(getData)
    }, [customerEmail, customerName, customerPhone])

    return (
        <DetailsTemplate
            codesProps={codesProps}
            fieldArray={fieldArray()}
            isAccepted={isAccepted}
            setIsAccepted={setIsAccepted}
            errorHandler={[
                showError,
                discountErrorMessage,
                formatErrorMessage,
                setShowError,
                submitClickedWithMissingDetails,
                isDiscountApplicable,
            ]}
            companyNameHandler={[companyName, setCompanyName]}
            contactNameHandler={[companyAdminName, setCompanyAdminName]}
            emailHandler={[customerEmail, setCustomerEmail]}
            nameHandler={[customerName, setCustomerName]}
            phoneHandler={[
                customerPhone,
                setCustomerPhone,
                landCode,
                landCodes,
                onChangeLandCode,
            ]}
            partner={partner}
            basisType={basisType}
            paymentButtonHandler={[canGoToPayment, price, onClickGoToPayment]}
            bookingReasonHandler={[
                selectedBookingReasons,
                bookingReasons,
                onClickBookingReason,
                canSetBookingReasons,
            ]}
            descriptionHandler={[
                problemDescription,
                setProblemDescription,
                canSetDescription,
            ]}
            parkingHandler={[parkingText, setParkingText, canSetParking]}
            specialCareHandler={[
                specialCare,
                setSpecialCare,
                canSetSpecialCare,
            ]}
            isCompanyBooking={isCompanyBooking}
            isCompanyLead={isCompanyLead}
            onClickBack={onClickBack}
            discountAmount={discountAmount}
            customerType={customerType}
            isDiscountSubmitable={isDiscountSubmitable}
            setIsDiscountSubmitable={setIsDiscountSubmitable}
            companySettings={companySettings}
            goalId={goal?.id}
            returningCustomerProps={returningCustomerProps}
        />
    )
}

export default DetailsPage
